/* HowItWorks — full marketing page rebuilt 2026-02-24 to match the
 * homepage + Why Invisique editorial polish.
 *
 * Layout (top to bottom):
 *   1. HERO        — asymmetric 2-col: left copy + dual CTAs · right lifestyle
 *                    photo with floating timeline chip + IDA Certified badge
 *   2. TIMELINE    — horizontal 5-stage journey ribbon with duration chips
 *   3. WHAT WE TREAT — 6 cases in a richer asymmetric layout w/ illustrations
 *   4. STEPS       — 5 alternating zig-zag blocks with step badges + bullets
 *   5. QUOTE       — patient quote with portrait + emotional anchor
 *   6. STATS BAND  — trust strip (treatments completed, avg time, satisfaction)
 *   7. CTA         — gradient block with dual buttons
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
    Scan, Package, SmileyWink, Camera, MapPin, Star,
    Sparkle, ShieldStar, Clock, CheckCircle, Quotes,
    Stethoscope, MagnifyingGlass,
} from "@phosphor-icons/react";


// -----------------------------------------------------------------------------
// Content (existing data preserved; enriched with duration + bullets)
// -----------------------------------------------------------------------------

const HERO_IMAGE = "/marketing/how-it-works-hero.png";

const STEPS = [
    {
        icon: MapPin,
        title: "Find a Certified Provider",
        duration: "Visit 1 · ~30 min",
        body: "Browse our network of board-certified doctors across India — filter by city to find one near you. Your first consultation is free.",
        bullets: [
            "500+ certified clinics across 50+ cities",
            "Free first consultation",
            "Flexible EMI options available",
        ],
        testid: "step-find-provider",
    },
    {
        icon: Scan,
        title: "Get Your 3D Digital Scan",
        duration: "Visit 1 · ~10 min",
        body: "Your doctor uses an intraoral 3D scanner to build a precise digital map of your teeth. No impressions, no mess — and you'll see a preview of your new smile before you walk out.",
        bullets: [
            "Painless intraoral 3D scan",
            "AI-generated smile preview",
            "Plan co-reviewed by your doctor and our Center of Excellence team",
        ],
        testid: "step-3d-scan",
    },
    {
        icon: Package,
        title: "Receive Your Custom Aligners",
        duration: "Day 7–10",
        body: "Your aligners are precision-crafted from your scan at our ISO 13485-certified facility and delivered directly to your clinic. New trays arrive every 1–2 weeks.",
        bullets: [
            "Made in India · delivered in 7–10 days",
            "Advanced multi-layer polymer shell for consistent, controlled tooth movement",
            "Manufactured to traceable quality standards",
        ],
        testid: "step-aligners",
    },
    {
        icon: Camera,
        title: "Track Progress Weekly",
        duration: "Months 1–10",
        body: "Wear your aligners 20–22 hrs/day. Log weekly photos in your patient portal, message your clinic anytime, and check in every 6–8 weeks.",
        bullets: [
            "In-app weekly check-ins",
            "Direct clinic chat",
            "Timely nudges from your doctor to keep you on track",
        ],
        testid: "step-track",
    },
    {
        icon: SmileyWink,
        title: "Reveal Your New Smile",
        duration: "Months 6–12",
        body: "Most treatments complete in 6–12 months. Maintain your results for life with the Invisique™ Infinite™ retainer programme.",
        bullets: [
            "Average completion: 8 months",
            "Lifetime retainer programme",
            "Free touch-up within 12 months of completion",
        ],
        testid: "step-reveal",
    },
];

/* ---------------------------------------------------------------------------
   Anatomical case icons (2026-02-25). The original Phosphor abstract icons
   (AlignCenterHorizontal, Minus, ArrowLineDown, etc.) don't communicate the
   actual orthodontic conditions. These inline SVGs use a shared "tooth row"
   visual language — upper row at full opacity, lower row at 40% — so the
   relationship between arches makes each condition immediately readable.
   Each tooth = ~6×10 rounded rectangle. viewBox 40×40 so the icons scale
   cleanly from 32 px to ~64 px without losing definition.
   --------------------------------------------------------------------------- */
const ToothIconBase = ({ children, size = 36 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        {children}
    </svg>
);

const CrowdingIcon = ({ size }) => (
    <ToothIconBase size={size}>
        {/* upper: 3 teeth, two heavily rotated to suggest overcrowding */}
        <rect x="4" y="7" width="8" height="14" rx="2" fill="currentColor" />
        <g transform="rotate(14 19 14)">
            <rect x="15" y="7" width="8" height="14" rx="2" fill="currentColor" />
        </g>
        <g transform="rotate(-12 28 14)">
            <rect x="24" y="7" width="8" height="14" rx="2" fill="currentColor" />
        </g>
        {/* small arc above to underscore "crowding" */}
        <path d="M 6 5 Q 20 2 34 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.5" />
        {/* lower: clean reference row */}
        <rect x="6" y="26" width="7" height="9" rx="1.5" fill="currentColor" opacity="0.3" />
        <rect x="16.5" y="26" width="7" height="9" rx="1.5" fill="currentColor" opacity="0.3" />
        <rect x="27" y="26" width="7" height="9" rx="1.5" fill="currentColor" opacity="0.3" />
    </ToothIconBase>
);

const SpacingIcon = ({ size }) => (
    <ToothIconBase size={size}>
        {/* upper: 3 widely-spaced teeth with explicit gap dots between them */}
        <rect x="3" y="7" width="8" height="14" rx="2" fill="currentColor" />
        <rect x="16" y="7" width="8" height="14" rx="2" fill="currentColor" />
        <rect x="29" y="7" width="8" height="14" rx="2" fill="currentColor" />
        {/* gap markers */}
        <circle cx="13.5" cy="14" r="1" fill="currentColor" opacity="0.45" />
        <circle cx="26.5" cy="14" r="1" fill="currentColor" opacity="0.45" />
        {/* lower: matching gaps */}
        <rect x="3" y="26" width="6" height="9" rx="1.5" fill="currentColor" opacity="0.3" />
        <rect x="14" y="26" width="6" height="9" rx="1.5" fill="currentColor" opacity="0.3" />
        <rect x="25" y="26" width="6" height="9" rx="1.5" fill="currentColor" opacity="0.3" />
        <rect x="36" y="26" width="1" height="9" rx="0.5" fill="currentColor" opacity="0.3" />
    </ToothIconBase>
);

const DeepBiteIcon = ({ size }) => (
    <ToothIconBase size={size}>
        {/* upper: EXAGGERATED long teeth dropping far down past midline */}
        <rect x="4" y="5" width="8" height="22" rx="2" fill="currentColor" />
        <rect x="16" y="5" width="8" height="22" rx="2" fill="currentColor" />
        <rect x="28" y="5" width="8" height="22" rx="2" fill="currentColor" />
        {/* lower: short, mostly covered */}
        <rect x="6" y="29" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.3" />
        <rect x="17" y="29" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.3" />
        <rect x="28" y="29" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.3" />
    </ToothIconBase>
);

const UnderbiteIcon = ({ size }) => (
    <ToothIconBase size={size}>
        {/* upper: SHORT, set back inside (lower is wider/taller) */}
        <rect x="10" y="8" width="6" height="10" rx="1.5" fill="currentColor" opacity="0.4" />
        <rect x="18" y="8" width="6" height="10" rx="1.5" fill="currentColor" opacity="0.4" />
        <rect x="26" y="8" width="6" height="10" rx="1.5" fill="currentColor" opacity="0.4" />
        {/* lower: WIDE, PROTRUDING — extends past upper on both sides + taller */}
        <rect x="3" y="22" width="9" height="14" rx="2" fill="currentColor" />
        <rect x="14.5" y="22" width="9" height="14" rx="2" fill="currentColor" />
        <rect x="26" y="22" width="9" height="14" rx="2" fill="currentColor" />
        {/* small upward arrow on lower to emphasise jut */}
        <path d="M 20 36 L 20 33 M 18 35 L 20 33 L 22 35" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </ToothIconBase>
);

const CrossbiteIcon = ({ size }) => (
    <ToothIconBase size={size}>
        {/* upper: shifted LEFT */}
        <rect x="2" y="7" width="8" height="13" rx="2" fill="currentColor" />
        <rect x="13" y="7" width="8" height="13" rx="2" fill="currentColor" />
        <rect x="24" y="7" width="8" height="13" rx="2" fill="currentColor" />
        {/* lower: shifted RIGHT */}
        <rect x="8" y="23" width="8" height="13" rx="2" fill="currentColor" opacity="0.32" />
        <rect x="19" y="23" width="8" height="13" rx="2" fill="currentColor" opacity="0.32" />
        <rect x="30" y="23" width="8" height="13" rx="2" fill="currentColor" opacity="0.32" />
        {/* crossing direction arrows */}
        <path d="M 33 21 L 36 21" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        <path d="M 34.5 19.5 L 36 21 L 34.5 22.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <path d="M 7 22 L 4 22" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity="0.6" />
        <path d="M 5.5 20.5 L 4 22 L 5.5 23.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
    </ToothIconBase>
);

const OpenBiteIcon = ({ size }) => (
    <ToothIconBase size={size}>
        {/* upper: front teeth shorter, back teeth long (creates obvious "smile gap") */}
        <rect x="3" y="5" width="7" height="17" rx="2" fill="currentColor" />
        <rect x="13" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
        <rect x="21" y="9" width="6" height="6" rx="1.5" fill="currentColor" />
        <rect x="30" y="5" width="7" height="17" rx="2" fill="currentColor" />
        {/* lower: mirror — front teeth shorter, back teeth long */}
        <rect x="3" y="22" width="7" height="13" rx="2" fill="currentColor" opacity="0.3" />
        <rect x="13" y="25" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.3" />
        <rect x="21" y="25" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.3" />
        <rect x="30" y="22" width="7" height="13" rx="2" fill="currentColor" opacity="0.3" />
        {/* horizontal "gap" line in the middle to scream the open bite */}
        <line x1="13" y1="20" x2="27" y2="20" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="2 2" opacity="0.55" />
    </ToothIconBase>
);

const CASES = [
    { Icon: CrowdingIcon,  title: "Crowding",  desc: "Not enough room in the jaw causes teeth to bunch, overlap, and twist, making them harder to clean." },
    { Icon: SpacingIcon,   title: "Spacing",   desc: "Extra gaps between teeth caused by missing teeth, small teeth, or a wide dental arch." },
    { Icon: DeepBiteIcon,  title: "Deep bite", desc: "Upper front teeth overlap the lower teeth significantly, can lead to wear and jaw joint pain." },
    { Icon: UnderbiteIcon, title: "Underbite", desc: "Lower teeth protrude past the upper, often from under- or overgrowth of the jaw." },
    { Icon: CrossbiteIcon, title: "Crossbite", desc: "Some upper teeth sit inside the lower, may cause wear, gum disease, asymmetric growth." },
    { Icon: OpenBiteIcon,  title: "Open bite", desc: "Upper and lower front teeth don't touch when closed, chewing and speech can suffer." },
];

/* iter100.207 — PATIENT_QUOTE is now admin-manageable via
   /admin/landing-page → Patient Spotlights tab (scope=how_it_works).
   Kept here as an offline fallback if the API call fails. Live data
   comes from GET /api/public/spotlights/how_it_works. */
const PATIENT_QUOTE_FALLBACK = {
    quote: "I started in February — by Diwali I was smiling for photos without thinking about it. My orthodontist saw every aligner before it shipped. That mattered.",
    name: "Aarushi K.",
    city_meta: "Mumbai",
    image_url: "/marketing/p88.png",
    badge_top: "Finished in",
    badge_bot: "9 months",
    verified_pill_label: "Verified patient",
    outcomes: [
        { value: "9 months", label: "Total treatment" },
        { value: "24 aligners", label: "Custom trays" },
        { value: "6 visits", label: "In-clinic check-ins" },
    ],
};

/* Three principles that turn the hero's "doctor-led + COE-validated" thesis
   into a scannable mental model before the visitor reaches the 5 steps.
   Filler band that replaces the retired "At a glance" timeline ribbon. */
const PRINCIPLES = [
    {
        icon: Stethoscope,
        title: "Designed by your doctor",
        desc: "Your plan is authored by the specialist who knows your case — not generated by an algorithm.",
        testid: "principle-designed",
    },
    {
        icon: ShieldStar,
        title: "Validated by our Center of Excellence",
        desc: "Every plan is independently reviewed by a second specialist before a single aligner is made.",
        testid: "principle-validated",
    },
    {
        icon: MagnifyingGlass,
        title: "Supervised through every aligner",
        desc: "Your provider monitors progress at every check-in. Course corrections are clinical, not automated.",
        testid: "principle-supervised",
    },
];


// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

export default function HowItWorks() {
    return (
        <div data-testid="how-it-works-page" className="overflow-x-hidden">
            <Hero />
            <DoctorLedPrinciples />
            <WhatWeTreat />
            <Steps />
            <PatientSpotlight />
            <FinalCta />
        </div>
    );
}


// -----------------------------------------------------------------------------
// 1) Hero — editorial, page-specific (homepage-only signals removed
//    on 2026-02-24 user feedback: Live counter, IDA badge, 4.9★ row,
//    dual CTAs and stat triplet stripped — they belong on the marketing
//    homepage, not on a process-explainer page. The hero now leads with
//    the page's actual job: explaining the journey. The "8 months avg"
//    chip stays because it directly supports the page's thesis.
// -----------------------------------------------------------------------------
function Hero() {
    return (
        <section className="relative overflow-hidden">
            <div className="absolute inset-0 blue-grid-bg opacity-50 mask-fade-b pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    {/* LEFT — copy + journey preview */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-6"
                    >
                        <div
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-bold tracking-[0.22em] uppercase mb-6"
                            data-testid="hero-overline"
                        >
                            <Sparkle size={13} weight="fill" /> The Invisique™ journey
                        </div>

                        <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.6rem] font-bold leading-[1.04] tracking-tight">
                            Precision treatment. Five structured stages. <span className="text-primary">One certified outcome.</span>
                        </h1>

                        <p className="text-lg text-muted-foreground mt-6 max-w-xl leading-relaxed">
                            Treatment timelines range from <span className="text-foreground font-semibold">6 to 12 months</span>,
                            determined by case complexity and clinical assessment.
                            Each phase is conducted under the direct supervision of your doctor and independently validated by our Center of Excellence — ensuring measurable, predictable results at every interval.
                        </p>

                        {/* Trust strip — 3 process pillars below the body copy
                            (added 2026-02-25 to match the Pricing + Why-Invisique
                            hero pattern, so every marketing-page hero is
                            self-contained and ends on tangible credibility). */}
                        <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-xl" data-testid="hiw-hero-trust-strip">
                            {[
                                { icon: Stethoscope,     title: "Doctor-led",  desc: "A clinician owns your plan, not an algorithm." },
                                { icon: MagnifyingGlass, title: "COE-validated",     desc: "Independent second-specialist review of every plan." },
                                { icon: Clock,           title: "6 to 12 months",    desc: "Most cases complete within a year." },
                            ].map((p) => (
                                <div
                                    key={p.title}
                                    className="flex items-start gap-2.5 px-3 py-2.5 rounded-2xl bg-card border border-border/60 hover:border-primary/30 transition-colors"
                                    data-testid={`hiw-hero-trust-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                >
                                    <div className="w-8 h-8 rounded-full bg-primary text-white grid place-items-center shadow-sm ring-2 ring-secondary/40 shrink-0 mt-0.5">
                                        <p.icon size={14} weight="fill" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-display text-[12px] font-bold leading-tight">{p.title}</div>
                                        <div className="text-[10.5px] text-muted-foreground leading-snug mt-0.5">{p.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* RIGHT — lifestyle photo, single thesis-supporting chip */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="lg:col-span-6 relative"
                    >
                        <div className="relative rounded-[28px] overflow-hidden bento-shadow">
                            <img
                                src={HERO_IMAGE}
                                alt="A patient holding a clear aligner, smiling at the result"
                                className="w-full h-full object-cover aspect-[4/5] lg:aspect-auto lg:max-h-[640px]"
                            />

                            {/* Journey duration chip — the ONE overlay that
                                directly serves a How-It-Works page's job. */}
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 0.45 }}
                                className="absolute bottom-5 left-5 right-5 sm:right-auto sm:max-w-[300px] rounded-2xl bg-white/95 backdrop-blur-xl border border-white/60 shadow-xl p-4"
                                data-testid="hero-journey-chip"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Clock size={14} weight="fill" className="text-primary" />
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                                        Average journey
                                    </span>
                                </div>
                                <p className="text-[15px] text-foreground leading-snug font-semibold">
                                    8 months from scan to new smile
                                </p>
                                <p className="text-[11px] text-muted-foreground mt-1.5">
                                    Some complete in as little as 6 months · complex cases in 12+
                                </p>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 2) Doctor-led principles — 3 cards expanding on the hero's thesis.
//    Replaces the gap left by the retired "At a glance" timeline ribbon.
//    Same 3 ideas the hero body promises (doctor supervision + COE
//    validation), broken into a scannable mental model BEFORE the visitor
//    reaches the 5 steps below.
// -----------------------------------------------------------------------------
function DoctorLedPrinciples() {
    return (
        <section className="relative py-20 lg:py-24 border-t border-border/60" data-testid="doctor-led-principles">
            <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 to-background pointer-events-none" aria-hidden="true" />
            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid lg:grid-cols-12 gap-8 items-end mb-12 lg:mb-14">
                    <div className="lg:col-span-7">
                        <div className="overline text-primary mb-3">Doctor-led, by design</div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.4rem] font-bold tracking-tight leading-[1.1]">
                            Three checkpoints stand between your scan <span className="text-primary">and your aligners.</span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-muted-foreground text-base leading-relaxed">
                        Every Invisique™ treatment is gated by trained clinicians — not just code.
                        Here's where they step in.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
                    {PRINCIPLES.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            className="relative bg-card rounded-2xl border border-border/70 p-6 lg:p-7 bento-shadow hover:border-primary/40 hover:-translate-y-0.5 transition-all overflow-hidden"
                            data-testid={p.testid}
                        >
                            {/* Soft corner glow */}
                            <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-primary/5 blur-2xl pointer-events-none" aria-hidden="true" />

                            <div className="relative">
                                <div className="flex items-center justify-between mb-5">
                                    <div className="w-12 h-12 rounded-2xl bg-primary/10 grid place-items-center">
                                        <p.icon size={22} weight="duotone" className="text-primary" />
                                    </div>
                                    <span className="font-display text-[2.4rem] font-bold text-primary/15 tabular-nums leading-none">
                                        0{i + 1}
                                    </span>
                                </div>
                                <h3 className="font-display text-lg lg:text-xl font-bold tracking-tight leading-tight mb-2">
                                    {p.title}
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    {p.desc}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 3) What we treat — 2026-02-25 redesigned per user feedback:
//    • LEFT col: tall hero image card (now taller, aspect-[4/5]) + the
//      "Free Smile Assessment" CTA sits BELOW the image (not in the grid).
//    • RIGHT col: all 6 condition cards in a 2×3 grid with custom anatomical
//      SVG icons (CrowdingIcon, SpacingIcon, …) replacing the abstract
//      Phosphor icons that didn't communicate the conditions.
// -----------------------------------------------------------------------------
function WhatWeTreat() {
    const FEATURED_IMAGE = "/marketing/p7.png";

    return (
        <section className="py-24 lg:py-32" data-testid="what-we-treat-section">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-14 lg:mb-20">
                    <div className="lg:col-span-7">
                        <div className="overline text-primary mb-4">What we treat</div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                            Engineered for every case, from mild crowding <span className="text-primary">to complex skeletal corrections.</span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-muted-foreground text-lg leading-relaxed">
                        Clear aligners are clinically proven to correct a wide range of orthodontic concerns.
                        Your doctor will confirm what's right for your case in the first consultation.
                    </p>
                </div>

                {/* Asymmetric layout: LEFT image, RIGHT 6-card grid, then a
                    FULL-WIDTH CTA below spanning the entire row (user feedback
                    2026-02-25: "make it full? covering the space below crossbite
                    and open bite cards"). */}
                <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                    {/* LEFT — image (bigger) */}
                    <div className="lg:col-span-5">
                        {/* Hero image card — taller portrait crop */}
                        <div className="relative rounded-3xl overflow-hidden bento-shadow aspect-[4/5]">
                            <img
                                src={FEATURED_IMAGE}
                                alt="Doctor reviewing a treatment plan"
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" aria-hidden="true" />
                            <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-7 text-white">
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-md text-[10px] tracking-[0.18em] uppercase font-bold mb-3">
                                    Doctor-Led, Every Step
                                </div>
                                <h3 className="font-display text-2xl lg:text-3xl font-bold leading-tight">
                                    Your treatment begins — and ends — with your doctor.
                                </h3>
                                <p className="text-sm text-white/80 mt-2 leading-relaxed max-w-md">
                                    Every plan is clinically assessed and supervised by a qualified specialist, not an algorithm.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — 6 condition cards in a 2×3 grid (single column on mobile) */}
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
                            {CASES.map((c, i) => (
                                <motion.div
                                    key={c.title}
                                    initial={{ opacity: 0, y: 15 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.06, duration: 0.45 }}
                                    className="p-5 bg-card rounded-2xl border border-border/70 hover:border-primary/40 hover:-translate-y-0.5 transition-all bento-shadow"
                                    data-testid={`case-${c.title.toLowerCase().replace(/\s/g, "-")}`}
                                >
                                    <div className="w-14 h-14 rounded-full bg-primary text-white grid place-items-center shadow-md ring-4 ring-secondary/30 mb-3.5">
                                        <c.Icon size={28} />
                                    </div>
                                    <h3 className="font-display text-base font-semibold mb-1.5 leading-tight">{c.title}</h3>
                                    <p className="text-xs text-muted-foreground leading-relaxed">{c.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* FULL-WIDTH CTA — spans the entire grid below both columns */}
                    <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="lg:col-span-12 relative overflow-hidden rounded-2xl bento-shadow border border-primary/15 bg-card group mt-2"
                        data-testid="what-we-treat-cta"
                    >
                        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-200/40 blur-3xl pointer-events-none" aria-hidden="true" />
                        <div className="absolute -bottom-16 -left-12 w-52 h-52 rounded-full bg-primary/10 blur-3xl pointer-events-none" aria-hidden="true" />

                        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-5 p-6 lg:p-7">
                            <div className="flex-1 min-w-0">
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200/80 text-amber-700 text-[10px] tracking-[0.2em] uppercase font-bold mb-2.5">
                                    <Sparkle size={11} weight="fill" /> Not sure if you qualify?
                                </div>
                                <h3 className="font-display text-lg lg:text-xl font-bold leading-tight tracking-tight">
                                    Take our free 60-second smile assessment.
                                </h3>
                                <p className="text-[13px] text-muted-foreground mt-1 leading-relaxed max-w-2xl">
                                    Tell us about your case and we'll tell you exactly which approach fits — no clinic visit required.
                                </p>
                            </div>
                            <Link to="/smile-assessment" className="relative group/btn shrink-0 w-full md:w-auto" data-testid="what-we-treat-cta-link">
                                <span className="absolute inset-0 rounded-full bg-amber-400/40 blur-md animate-pulse pointer-events-none" aria-hidden="true" />
                                <span className="absolute -top-2 -right-2 z-10 px-2 py-0.5 rounded-full bg-amber-400 text-foreground text-[10px] font-bold tracking-wider shadow-md">
                                    FREE
                                </span>
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="relative h-12 px-6 rounded-full border-2 border-amber-400 bg-white hover:bg-amber-50 text-foreground font-semibold transition-all group-hover/btn:scale-[1.02] w-full md:w-auto"
                                    data-testid="what-we-treat-cta-button"
                                >
                                    <Sparkle size={14} weight="fill" className="mr-2 text-amber-500" />
                                    FREE SMILE ASSESSMENT
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 4) Steps — compact vertical stepper (5 dense rows replacing the bulky
//    full-screen zig-zag blocks on 2026-02-25 — user feedback: "too bulky").
//    Each row keeps the icon · step number · title · duration · body · bullets,
//    but renders as a single compact card so the section fits in ~one viewport
//    instead of 5.
// -----------------------------------------------------------------------------
function Steps() {
    return (
        <section className="py-24 lg:py-28 bg-secondary/30 border-y border-border/60" data-testid="steps-section">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <div className="max-w-3xl mb-10 lg:mb-12">
                    <div className="overline text-primary mb-4">The process</div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.5rem] font-bold tracking-tight leading-[1.1]">
                        Simple steps. Clear roles. <span className="text-primary">No guesswork.</span>
                    </h2>
                    <p className="text-base text-muted-foreground mt-4 leading-relaxed">
                        Your doctor manages your treatment.
                    </p>
                </div>

                <div className="relative space-y-5">
                    {/* Vertical connector line behind the step circles (desktop only).
                        Sits at left-[2.5rem] = halfway across the larger 80×80 circle
                        centered inside col-span-1 of the row grid. */}
                    <div
                        className="hidden md:block absolute left-[2.5rem] top-12 bottom-12 w-px bg-gradient-to-b from-primary/30 via-primary/20 to-primary/30"
                        aria-hidden="true"
                    />

                    {STEPS.map((s, i) => (
                        <motion.div
                            key={s.title}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.45, delay: i * 0.05 }}
                            className="relative bg-card border border-border/70 rounded-2xl p-5 sm:p-6 bento-shadow hover:border-primary/40 hover:-translate-y-0.5 transition-all"
                            data-testid={s.testid}
                        >
                            <div className="grid md:grid-cols-12 gap-4 md:gap-6 items-center">
                                {/* Step circle — enlarged 60→80px to carry the row now
                                    that the photo column has been removed. Amber numeric
                                    badge keeps the step number scannable at a glance. */}
                                <div className="md:col-span-1 flex md:justify-center">
                                    <div className="relative w-[80px] h-[80px] rounded-full bg-primary text-white grid place-items-center shadow-md ring-4 ring-secondary/30">
                                        <s.icon size={28} weight="fill" />
                                        <div className="absolute -top-1.5 -right-1.5 w-7 h-7 rounded-full bg-amber-400 text-amber-950 text-[11px] font-bold grid place-items-center shadow tabular-nums">
                                            {i + 1}
                                        </div>
                                    </div>
                                </div>

                                {/* Copy — expanded col-span-11 (was 8) since the thumbnail
                                    column was removed. Title row keeps the inline duration
                                    chip; bullets stay as horizontal CheckCircle pills. */}
                                <div className="md:col-span-11">
                                    <div className="flex flex-wrap items-center gap-2.5 mb-2">
                                        <h3 className="font-display text-lg sm:text-xl font-bold tracking-tight leading-tight">
                                            {s.title}
                                        </h3>
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold tracking-wider uppercase">
                                            <Clock size={10} weight="fill" /> {s.duration}
                                        </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground leading-relaxed mb-3 max-w-3xl">
                                        {s.body}
                                    </p>
                                    <ul className="flex flex-wrap gap-x-5 gap-y-1.5">
                                        {s.bullets.map((b) => (
                                            <li key={b} className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-foreground/85">
                                                <CheckCircle size={14} weight="fill" className="text-primary shrink-0" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 5) Patient Spotlight — magazine-style feature card.
//    Replaces the older plain 2-col layout flagged by the user 2026-02-25.
//    Uses a contrasting light cream + primary teal palette (distinct from
//    the dark teal Why-Invisique testimonial so the two pages don't echo).
// -----------------------------------------------------------------------------
function PatientSpotlight() {
    const [q, setQ] = useState(PATIENT_QUOTE_FALLBACK);
    useEffect(() => {
        api.get("/public/spotlights/how_it_works")
            .then((r) => {
                if (r.data?.item) setQ(r.data.item);
            })
            .catch(() => { /* fallback stays */ });
    }, []);
    return (
        <section
            className="relative py-24 lg:py-32 overflow-hidden"
            data-testid="patient-spotlight"
        >
            {/* Soft warm background wash + grid pattern */}
            <div
                className="absolute inset-0 bg-gradient-to-br from-amber-50/60 via-background to-primary/5"
                aria-hidden="true"
            />
            <div
                className="absolute inset-0 opacity-[0.04]"
                style={{
                    backgroundImage: "linear-gradient(rgba(0,62,80,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(0,62,80,0.6) 1px, transparent 1px)",
                    backgroundSize: "44px 44px",
                }}
                aria-hidden="true"
            />

            <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
                {/* Overline */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5 }}
                    className="mb-12 lg:mb-14"
                >
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-bold tracking-[0.22em] uppercase">
                        <Sparkle size={11} weight="fill" /> A patient story
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.5rem] font-bold tracking-tight leading-[1.1] mt-5 max-w-3xl">
                        Real journeys, <span className="text-primary">finished on time.</span>
                    </h2>
                </motion.div>

                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-stretch">
                    {/* LEFT — portrait stamp */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.65 }}
                        className="lg:col-span-5 relative"
                    >
                        <div className="relative w-full max-w-[400px] mx-auto lg:mx-0">
                            {/* Primary teal stamp shifted behind */}
                            <div
                                className="absolute top-6 left-6 w-full h-full rounded-[28px] bg-primary"
                                aria-hidden="true"
                            />
                            {/* Soft amber accent square */}
                            <div
                                className="absolute -top-3 -left-3 w-20 h-20 rounded-2xl bg-amber-300/40 border border-amber-300/50"
                                aria-hidden="true"
                            />

                            {/* Portrait */}
                            <div className="relative rounded-[28px] overflow-hidden bento-shadow ring-1 ring-border/50">
                                <img
                                    src={q.image_url}
                                    alt={q.name}
                                    className="w-full aspect-[4/5] object-cover"
                                    loading="lazy"
                                    data-testid="patient-spotlight-portrait"
                                />
                                {/* Verified badge — bottom-right corner */}
                                <div className="absolute bottom-4 right-4">
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-md border border-white/40 shadow-lg">
                                        <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                                        <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-foreground">
                                            {q.verified_pill_label || "Verified patient"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Floating outcome badge — top-right of portrait */}
                            {(q.badge_top || q.badge_bot) && (
                                <div className="absolute -top-4 -right-4 sm:right-2 px-4 py-3 rounded-2xl bg-card border border-border/70 shadow-xl" data-testid="patient-spotlight-badge">
                                    <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">{q.badge_top}</div>
                                    <div className="font-display text-2xl font-bold tracking-tight text-primary tabular-nums leading-none mt-1">
                                        {q.badge_bot}
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>

                    {/* RIGHT — quote + outcome strip */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.65, delay: 0.15 }}
                        className="lg:col-span-7 relative flex flex-col justify-between"
                    >
                        {/* Quote */}
                        <div className="relative">
                            <Quotes
                                size={120}
                                weight="fill"
                                className="absolute -top-8 -left-2 text-primary/10 pointer-events-none select-none hidden sm:block"
                                aria-hidden="true"
                            />
                            <p className="relative font-display text-2xl sm:text-3xl lg:text-[2.1rem] leading-[1.25] tracking-tight font-medium text-foreground" data-testid="patient-spotlight-quote">
                                &ldquo;{q.quote}&rdquo;
                            </p>

                            {/* Byline */}
                            <div className="mt-7 flex items-center gap-3">
                                <div className="w-10 h-px bg-primary/50" aria-hidden="true" />
                                <div>
                                    <div className="font-semibold text-base text-foreground" data-testid="patient-spotlight-name">{q.name}</div>
                                    <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5" data-testid="patient-spotlight-meta">
                                        <MapPin size={12} weight="fill" className="text-primary/60" />
                                        {q.city_meta || q.city}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Outcome strip */}
                        {(q.outcomes || []).length > 0 && (
                            <div
                                className="mt-8 grid grid-cols-3 gap-4 pt-6 border-t border-primary/15"
                                data-testid="patient-spotlight-outcomes"
                            >
                                {q.outcomes.map((o) => (
                                    <div key={o.label}>
                                        <div className="font-display text-xl sm:text-2xl font-bold tracking-tight text-foreground tabular-nums leading-none">
                                            {o.value}
                                        </div>
                                        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mt-2 leading-snug">
                                            {o.label}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 6) Final CTA — 2026-02-25: rebuilt to mirror the Landing-page `ba-cta-banner`
//    exactly (the rich light-card banner pattern with amber "REAL PATIENTS"
//    overline, two-clause headline, 3-chip value strip, and a stacked dual-CTA
//    column on the right: a primary TEAL gradient simulator button with a
//    shimmer glow, plus a secondary amber-bordered FIND-AN-ORTHODONTIST button
//    with the FREE CONSULT pill). User request: "make it like the attached image".
// -----------------------------------------------------------------------------
function FinalCta() {
    return (
        <section className="py-20 lg:py-24" data-testid="final-cta-section">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="relative overflow-hidden rounded-3xl bg-white border border-primary/15 p-7 sm:p-9 lg:p-11 bento-shadow"
                    data-testid="hiw-final-cta"
                >
                    {/* Warm halo accents (mirrors the homepage ba-cta-banner) */}
                    <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-amber-200/40 blur-3xl pointer-events-none" aria-hidden="true" />
                    <div className="absolute -bottom-24 -left-16 w-56 h-56 rounded-full bg-primary/10 blur-3xl pointer-events-none" aria-hidden="true" />

                    <div className="relative grid lg:grid-cols-12 gap-6 items-center">
                        {/* LEFT — overline + two-clause headline + body + 3 value chips */}
                        <div className="lg:col-span-7">
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200/80 text-amber-700 text-[11px] font-bold tracking-[0.2em] uppercase mb-4">
                                <Star size={11} weight="fill" /> Real patients · Real results
                            </div>
                            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15]">
                                Could this be{" "}
                                <span className="text-primary">your smile next?</span>
                            </h3>
                            <p className="text-muted-foreground mt-3 max-w-xl text-sm sm:text-base leading-relaxed">
                                Preview your post-Invisique™ smile with our AI in 10 seconds, or get a personalised cost estimate from a certified doctor near you.
                            </p>

                            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-foreground/75">
                                {["10-second AI preview", "Free first consultation", "500+ certified clinics"].map((v) => (
                                    <div key={v} className="flex items-center gap-1.5">
                                        <CheckCircle size={14} weight="fill" className="text-primary" />
                                        <span>{v}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* RIGHT — stacked CTAs */}
                        <div className="lg:col-span-5 flex flex-col gap-3 lg:items-stretch">
                            <Link to="/smile-simulator" className="relative group" data-testid="hiw-cta-simulator-link">
                                <span
                                    className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary via-cyan-400 to-primary bg-[length:200%_100%] opacity-70 blur-md group-hover:opacity-100 transition-opacity"
                                    style={{ animation: "shimmer 3s linear infinite" }}
                                    aria-hidden="true"
                                />
                                <Button
                                    size="lg"
                                    className="relative w-full h-12 rounded-full bg-gradient-to-r from-primary to-[#003E50] text-white font-semibold tracking-wide hover:from-[#003E50] hover:to-primary transition-all group-hover:scale-[1.02] shadow-lg"
                                    data-testid="hiw-cta-simulator-btn"
                                >
                                    <Sparkle size={15} weight="fill" className="mr-2 text-cyan-200" />
                                    TRY AI SMILE SIMULATOR
                                </Button>
                            </Link>
                            <Link to="/find-doctor" className="relative group" data-testid="hiw-cta-find-link">
                                <span className="absolute inset-0 rounded-full bg-amber-400/40 blur-md animate-pulse pointer-events-none" aria-hidden="true" />
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="relative w-full h-12 rounded-full border-2 border-amber-400 bg-white hover:bg-amber-50 text-foreground font-semibold tracking-wide transition-all group-hover:scale-[1.02]"
                                    data-testid="hiw-find-btn"
                                >
                                    <MapPin size={15} weight="fill" className="mr-2 text-amber-500" />
                                    FIND A DOCTOR
                                </Button>
                            </Link>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
function Stat({ value, label }) {
    return (
        <div>
            <div className="font-display text-2xl font-bold text-foreground tabular-nums leading-none">{value}</div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold mt-1.5">{label}</div>
        </div>
    );
}

function Divider() {
    return <span className="hidden sm:inline-block w-px h-8 bg-border" aria-hidden="true" />;
}
